import { gql, useQuery } from "@apollo/client";
import { useState } from "react";
import { FaLanguage, FaTableTennis } from "react-icons/fa";
import ImageGallery from "../../../../../components/Atoms/ImageGallery";
import Loader from "../../../../../components/Atoms/Loader";
import { Iso639 } from "../../../../../graphql/operations";
import Avatar from "../../../../Atoms/Avatar";
import { CloudinaryImage } from "../../../../Atoms/Cloudinary";
import ModalScreen from "../../../../Atoms/ModalScreen";
import TextCollapse from "../../../../Atoms/TextCollapse";
import TourSignIcon from "../../../../Atoms/TourSignIcon";
import ReviewStars from "../../../../Templates/Guide/GuideReview/ReviewStars";
import styles from "./GuideInfo.module.scss";

interface Props {
  guideId: string;
}

export default function GuideInfo({ guideId }: Props) {
  const [shouldGalleryOpen, setGalleryOpen] = useState(false);
  const [viewingImage, setViewingImage] = useState({
    src: "",
    modalOpen: false,
  });

  const { loading, error, data } = useQuery(GET_GUIDE, {
    variables: {
      id: guideId,
      language: Iso639.En,
    },
  });

  if (loading) {
    return <Loader />;
  }
  if (error || !data || !data.guide) {
    return "";
  }
  const { guide } = data;
  const { numTours } = data.guide;

  return (
    <div className={styles.GuideInfoContainer}>
      <div className={styles.Hero}>
        <div className={styles["HeroItem"]}>
          <Avatar name={guide.nickName} size={200} src={guide.profilePic} />
          <div className={styles["GuideStats"]}>
            <div className={styles["GuideStatItem"]}>
              <span>{numTours}</span>
              <span>Tours Guided</span>
            </div>
            <div className={styles["GuideStatItem"]}>
              <span>{guide.numCustomers}</span>
              <span>Customers</span>
            </div>
            <div className={styles["GuideStatItem"]}>
              <span>{guide.reviews.totalRecords}</span>
              <span>Reviews</span>
            </div>
          </div>
        </div>
        <div className={styles["HeroItem"]}>
          <div className={styles["GuideStars"]}>
            <ReviewStars
              rating={guide.reviews?.avgStars}
              size={18}
              style={{
                marginTop: 20,
              }}
            />
            <p>
              <TourSignIcon
                size={24}
                style={{
                  position: "relative",
                  top: 4,
                  marginRight: 3,
                }}
              />{" "}
              MagicalTrip Tour Guide
            </p>
          </div>
          <h1>{guide.nickName}</h1>
          <div className={styles["HeaderBorder"]} />

          {guide.translation?.description && (
            <div
              style={{
                color: "#777778",
              }}
            >
              <TextCollapse
                maxlength={190}
                text={guide.translation?.description}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.SecondRow}>
        <div
          className={styles.GallerySection}
          style={
            guide.media && guide.media.length === 0 ? { display: "none" } : null
          }
        >
          <div className={styles["GalleryImgContainer"]}>
            {guide.media &&
              guide.media.length > 0 &&
              guide.media.slice(0, 6).map((m, index) => {
                return (
                  <div
                    key={index}
                    className={styles["GalleryImg"]}
                    onClick={() =>
                      setViewingImage({
                        modalOpen: true,
                        src: m.url,
                      })
                    }
                  >
                    <CloudinaryImage
                      src={m.url}
                      transformations={[`w_200`, `h_200`, "c_fill"]}
                    />
                  </div>
                );
              })}
          </div>
          {data.guide.media.length > 5 && (
            <button
              className={styles["AltBtnPhoto"]}
              onClick={() => setGalleryOpen(true)}
            >
              View Photos
            </button>
          )}
          <div
            style={{
              display: "none",
            }}
          >
            <ImageGallery
              setGalleryOpen={setGalleryOpen}
              openGallery={shouldGalleryOpen}
              photos={data.guide.media}
            />
          </div>
        </div>
        <div className={styles.GuideInterests}>
          {guide.languages && guide.languages.length > 0 && (
            <>
              <FaLanguage
                size={100}
                className={styles["LanguageIcon"]}
                color="#EE394B"
              />
              <h3>Spoken Languages</h3>
              <div className={styles["HeaderBorder"]} />
              {guide.languages.map((l, i) => (
                <span key={i}> {l.name}</span>
              ))}
            </>
          )}

          {guide.translation?.hobbies &&
            guide.translation?.hobbies.length > 0 && (
              <>
                <FaTableTennis
                  size={60}
                  className={styles["TennisIcon"]}
                  color="#EE394B"
                />
                <h3>My Hobbies</h3>
                <div className={styles["HeaderBorder"]} />
                {guide.translation.hobbies.map((h, index) => {
                  return <span key={index}>{h}</span>;
                })}
              </>
            )}
        </div>
      </div>
      <a
        href={`/guide-profile/${guide.id}`}
        className={styles.SeeMoreBtn}
        target="_blank"
      >
        See More
      </a>
      {viewingImage.modalOpen && (
        <ModalScreen
          openVideo={viewingImage.modalOpen}
          onClose={() => {
            setViewingImage({ ...viewingImage, modalOpen: false });
          }}
        >
          <div className={styles["ViewImageModalContainer"]}>
            <div>
              <CloudinaryImage
                src={viewingImage.src}
                transformations={["c_fill"]}
              />
            </div>
          </div>
        </ModalScreen>
      )}
    </div>
  );
}

const GET_GUIDE = gql`
  query GuideProfileQuery($id: ID!, $language: Iso639!) {
    guide(id: $id) {
      id
      numCustomers
      translation(lang: $language) {
        description
        hobbies
      }
      nickName
      profilePic
      introVideo

      languages {
        name
      }
      media {
        type
        url
      }

      reviews {
        avgStars
        totalRecords
        records {
          stars
          text
          date
          userName
          profileImageUrl
        }
      }
      numTours
    }
  }
`;
